import { Injectable, inject, NgZone } from '@angular/core';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class UnhandledPromiseRejectionHandler {
  private errorHandlerService = inject(ErrorHandlerService);
  private ngZone = inject(NgZone);
  private isInitialized = false;

  /**
   * Initialize global promise rejection handler
   */
  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    // Handle unhandled promise rejections
    window.addEventListener(
      'unhandledrejection',
      (event: PromiseRejectionEvent) => {
        console.error('🚨 Unhandled Promise Rejection:', event.reason);

        // Prevent the default browser console error
        event.preventDefault();

        // Use our error handler
        this.ngZone.run(() => {
          this.errorHandlerService.handleError(event.reason, {
            component: 'UnhandledPromiseRejectionHandler',
            action: 'promise_rejection',
            promiseRejection: true,
            originalEvent: event.type,
          });
        });
      }
    );

    // Handle general JavaScript errors
    window.addEventListener('error', (event: ErrorEvent) => {
      console.error('🚨 Unhandled JavaScript Error:', event.error);

      // Use our error handler
      this.ngZone.run(() => {
        this.errorHandlerService.handleError(event.error || event.message, {
          component: 'UnhandledJavaScriptErrorHandler',
          action: 'javascript_error',
          filename: event.filename,
          lineNumber: event.lineno,
          columnNumber: event.colno,
          originalEvent: event.type,
        });
      });
    });

    console.log('✅ Global error handlers initialized');
  }

  /**
   * Cleanup event listeners (useful for testing or if needed)
   */
  destroy(): void {
    // Note: In a real application, you might want to store references to the handlers
    // so you can properly remove them. For now, we'll just mark as not initialized.
    this.isInitialized = false;
  }
}
